/**
 * BackfillLogPanel - Reusable component for displaying real-time log output
 *
 * Features:
 * - Monospace font scrollable log container
 * - Auto-scroll to bottom on new entries
 * - Color-coded log levels
 * - Timestamps on each entry
 */

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { BackfillLogEntry } from '@/types/backfill';

interface BackfillLogPanelProps {
  logs: BackfillLogEntry[];
  className?: string;
  maxHeight?: string;
}

export function BackfillLogPanel({ logs, className, maxHeight = '300px' }: BackfillLogPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs.length]);

  const getLevelColor = (level: BackfillLogEntry['level']) => {
    switch (level) {
      case 'success':
        return 'text-[#8FB560]';
      case 'warning':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-muted-foreground';
    }
  };

  const getLevelIcon = (level: BackfillLogEntry['level']) => {
    switch (level) {
      case 'success':
        return '[OK]';
      case 'warning':
        return '[WARN]';
      case 'error':
        return '[ERR]';
      default:
        return '[INFO]';
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (logs.length === 0) {
    return (
      <div
        className={cn(
          'bg-background/50 border border-border rounded-lg p-4 font-mono text-xs text-muted-foreground',
          className
        )}
        style={{ maxHeight }}
      >
        Waiting for backfill to start...
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'bg-background/50 border border-border rounded-lg overflow-y-auto font-mono text-xs',
        className
      )}
      style={{ maxHeight }}
    >
      <div ref={scrollRef} className="p-3 space-y-1">
        {logs.map((log, index) => (
          <div key={index} className="flex gap-2">
            <span className="text-muted-foreground/60 flex-shrink-0">
              {formatTimestamp(log.timestamp)}
            </span>
            <span className={cn('flex-shrink-0 w-12', getLevelColor(log.level))}>
              {getLevelIcon(log.level)}
            </span>
            <span className={getLevelColor(log.level)}>
              {log.unitCode && (
                <span className="text-foreground font-semibold mr-1">[{log.unitCode}]</span>
              )}
              {log.message}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
